import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Settings } from "lucide-react";
import { Alert, Button, Drawer, SegmentedControl, Tabs } from "@/shared/rd";
import { PageHeaderActions } from "@/shared/layout/PageHeaderActions";
import { useSearchParams } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useNavigationGuard } from "@/lib/navigation/navigationGuard";
import { useWhatsappQuery, whatsappFailureKey } from "../services/useWhatsapp";
import {
  changeParams,
  changeMessageSource,
  dateDefaults,
  readMessageSource,
  queryFor,
  readDates,
  readTab,
  tabs,
} from "../services/search-params";
import { AccountPanel } from "../components/AccountPanel";
import { BillingPanel } from "../components/BillingPanel";
import { QueryState } from "../components/Shared";
import { WhatsappFilterContext } from "../components/filter-context";
import { OverviewPanel } from "../components/OverviewPanel";
import { SetupCard } from "../components/SetupCard";
import { Filters } from "../components/Filters";
import { AttemptsPanel } from "../components/AttemptsPanel";
import type { WhatsappUsageRow } from "../types/whatsapp.types";
import { MessagesPanel } from "../components/MessagesPanel";
import { TemplatesPanel } from "../components/TemplatesPanel";
import { UsagePanel } from "../components/UsagePanel";
const copy = keys.whatsapp;
function cursorTrail(params: URLSearchParams): string[] {
  try {
    const value: unknown = JSON.parse(params.get("cursorTrail") ?? "[]");
    return Array.isArray(value) &&
      value.length <= 500 &&
      value.every((item) => typeof item === "string")
      ? value
      : [];
  } catch {
    return [];
  }
}
const VIEW_PARAMS = new Set(["tab", "source", "groupBy", "page", "after", "cursorTrail", "message"]);
const DATE_PARAMS = new Set(["dateFrom", "dateTo"]);

export function WhatsappPage() {
  const t = useTranslations();
  const [params, setParams] = useSearchParams();
  const tab = readTab(params);
  const source = readMessageSource(params);
  const guard = useNavigationGuard();
  const client = useQueryClient();
  const account = useWhatsappQuery("account");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const query = queryFor(tab, params);
  const update = (next: URLSearchParams) => {
    if (guard) guard.promptDiscard(() => setParams(next));
    else setParams(next);
  };
  // Anything in the URL besides the view itself (tab, source, grouping,
  // paging, the open record) narrows the lists; clearing keeps the view.
  const filtered = [...params.keys()].some(
    (key) => !VIEW_PARAMS.has(key) && Boolean(params.get(key)),
  );
  // The date range alone is a period, not a filter: an empty result then
  // offers the widest range rather than "Clear filters".
  const narrowed = [...params.keys()].some(
    (key) => !VIEW_PARAMS.has(key) && !DATE_PARAMS.has(key) && Boolean(params.get(key)),
  );
  const dates = readDates(params);
  const widest = dateDefaults(new Date(), 90);
  const widenPeriod =
    tab !== "templates" &&
    !narrowed &&
    (dates.dateFrom !== widest.dateFrom || dates.dateTo !== widest.dateTo)
      ? () => update(changeParams(params, { ...widest, message: null }))
      : undefined;
  const clearFilters = () =>
    update(
      new URLSearchParams({
        tab,
        ...(tab === "messages" ? { source } : {}),
        ...(params.get("groupBy") ? { groupBy: params.get("groupBy") ?? "" } : {}),
      }),
    );
  function nextPage(after: string) {
    update(
      changeParams(
        params,
        {
          after,
          message: null,
          cursorTrail: JSON.stringify([
            ...cursorTrail(params),
            params.get("after") ?? "",
          ]),
        },
        false,
      ),
    );
  }
  function previousPage() {
    const trail = cursorTrail(params);
    const after = trail.pop() ?? null;
    update(
      changeParams(
        params,
        {
          after,
          message: null,
          cursorTrail: trail.length ? JSON.stringify(trail) : null,
        },
        false,
      ),
    );
  }
  function refreshMessages() {
    const next = changeParams(params, { message: null });
    const refresh = () => {
      void client
        .invalidateQueries({
          queryKey: [
            "admin",
            "whatsapp",
            tab === "usage"
              ? "usage"
              : source === "meta"
                ? "history"
                : "messages",
            queryFor(tab, next),
          ],
          exact: true,
        })
        .catch(() => undefined);
      setParams(next);
    };
    if (guard) guard.promptDiscard(refresh);
    else refresh();
  }
  const onPage = (page: number) =>
    update(changeParams(params, { page: String(page), message: null }, false));
  function drillUsage(
    row: WhatsappUsageRow,
    groupBy: "seller" | "store",
    target: "messages" | "usage",
  ) {
    const next = new URLSearchParams(queryFor("usage", params));
    for (const field of [
      "page",
      "limit",
      "search",
      "groupBy",
      "sellerId",
      "storeId",
      "unattributed",
    ])
      next.delete(field);
    next.set("tab", target);
    if (target === "usage") next.set("groupBy", "store");
    else next.set("source", "trioty");
    if (row.unattributed) next.set("unattributed", "true");
    else if (row.id) {
      if (groupBy === "seller") {
        next.set("sellerId", row.id);
        if (row.name) next.set("sellerName", row.name);
      } else {
        next.set("storeId", row.id);
        if (row.name) next.set("storeName", row.name);
        if (row.sellerId) next.set("sellerId", row.sellerId);
        if (row.sellerName) next.set("sellerName", row.sellerName);
      }
    }
    update(next);
  }
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
      <PageHeaderActions>
        <Button
          variant="secondary"
          size="sm"
          icon={Settings}
          disabled={!account.data}
          onClick={() => setSettingsOpen(true)}
        >
          {t(copy.settings)}
        </Button>
      </PageHeaderActions>
      <div style={{ minWidth: 0, overflowX: "auto" }}>
        <Tabs
          value={tab}
          tabs={tabs.map((value) => ({ value, label: t(copy[value]) }))}
          onChange={(next) =>
            update(
              new URLSearchParams({
                ...(next === "templates" ? {} : readDates(params)),
                tab: next,
              }),
            )
          }
          label={t(copy.nav)}
        />
      </div>
      {tab === "messages" && (
        <div>
          <SegmentedControl
            value={source}
            label={t(copy.messageSource)}
            options={[
              { value: "trioty", label: t(copy.triotyMessages) },
              { value: "meta", label: t(copy.metaUpdates) },
            ]}
            onChange={(value) =>
              update(changeMessageSource(params, value === "meta" ? "meta" : "trioty"))
            }
          />
        </div>
      )}
      <Filters
        key={`${tab}-${params.toString()}`}
        tab={tab}
        params={params}
        onChange={update}
      />
      <WhatsappFilterContext.Provider value={{ filtered, clear: clearFilters, narrowed, widenPeriod }}>
      <QueryState
        loading={account.isLoading}
        error={account.data ? null : account.error}
        retry={account.refetch}
        page={t(copy.account)}
        rows={6}
      >
        {account.data && (
          <>
            {tab === "overview" && (
              <OverviewPanel
                query={query}
                account={account.data}
                onSettings={() => setSettingsOpen(true)}
                onTemplates={() =>
                  update(
                    changeParams(params, { tab: "templates", message: null }),
                  )
                }
                onMessages={() =>
                  update(
                    new URLSearchParams({
                      ...readDates(params),
                      tab: "messages",
                      source: "meta",
                    }),
                  )
                }
              />
            )}
            {!account.data.readiness.managementReady &&
              (tab === "templates" || tab === "billing") && (
                <SetupCard
                  readiness={account.data.readiness}
                  onSettings={() => setSettingsOpen(true)}
                />
              )}
            {tab === "templates" && account.data.readiness.managementReady && (
              <TemplatesPanel
                account={account.data}
                query={query}
                after={params.get("after")}
                onNext={nextPage}
                onPrevious={previousPage}
              />
            )}
            {tab === "messages" && (
              <>
                {(!account.data.readiness.appSecretConfigured ||
                  !account.data.readiness.webhookVerifyTokenConfigured ||
                  !account.data.readiness.businessAccountIdConfigured ||
                  !account.data.readiness.phoneNumberIdConfigured) && (
                  <Alert
                    tone="warn"
                    title={t("whatsapp.historySetupTitle")}
                    action={
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setSettingsOpen(true)}
                      >
                        {t(copy.settings)}
                      </Button>
                    }
                  >
                    {t(copy.historySetupMissing)}
                  </Alert>
                )}
                {source === "trioty" ? (
                  <AttemptsPanel
                    query={query}
                    messageId={params.get("message")}
                    onMessage={(id) =>
                      update(changeParams(params, { message: id }, false))
                    }
                    onPage={onPage}
                    onRefresh={refreshMessages}
                  />
                ) : (
                  <MessagesPanel
                    query={query}
                    messageId={params.get("message")}
                    onMessage={(id) =>
                      update(changeParams(params, { message: id }, false))
                    }
                    onNext={nextPage}
                    onPrevious={previousPage}
                    onRefresh={refreshMessages}
                    hasPrevious={
                      cursorTrail(params).length > 0 ||
                      Boolean(params.get("after"))
                    }
                  />
                )}
              </>
            )}
            {tab === "usage" && (
              <UsagePanel
                query={query}
                onPage={onPage}
                onDrill={drillUsage}
                onRefresh={refreshMessages}
              />
            )}
            {tab === "billing" && account.data.readiness.managementReady && (
              <BillingPanel
                query={query}
                category={params.get("billingCategory") ?? ""}
                onCategory={(value) =>
                  update(
                    changeParams(params, { billingCategory: value }, false),
                  )
                }
              />
            )}
          </>
        )}
      </QueryState>
      </WhatsappFilterContext.Provider>
      {/* A failed background refresh keeps the loaded account on screen. */}
      {account.data && account.error && (
        <Alert
          tone="error"
          title={t("whatsapp.accountRefreshFailed")}
          action={
            <Button
              variant="secondary"
              size="sm"
              loading={account.isRefetching}
              loadingLabel={t(keys.shell.retrying)}
              onClick={() => void account.refetch()}
            >
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {t(whatsappFailureKey(account.error))}
        </Alert>
      )}
      {/* A failed background refresh must not unmount an edited settings draft. */}
      {settingsOpen && account.data && (
        <Drawer
          open
          crumb={`${t(copy.nav)} / ${t(copy.settings)}`}
          badge="phone"
          title={t(copy.account)}
          sub={t(copy.connectionSettings)}
          width={560}
          closeLabel={t(copy.close)}
          onClose={() => {
            const close = () => setSettingsOpen(false)
            if (guard) guard.promptDiscard(close);
            else close();
          }}
        >
          <AccountPanel
            key={account.data.businessAccountId ?? "unconfigured"}
            account={account.data}
          />
        </Drawer>
      )}
    </section>
  );
}
